import { throttle } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';

import useLayoutEffect from '@swf/core/hooks/useIsomorphicLayoutEffect';

const RESIZE_THROTTLE = 20;

interface CarouselProps {
  children: React.ReactNode;
  gap: number;
}

interface CarouselState {
  offset: number;
  page: number;
  isLeftEnd: boolean;
  isRightEnd: boolean;
  scrollLeft: () => void;
  scrollRight: () => void;
  setPagedOffset: (nextPage: number) => void;
  carouselRef: React.RefObject<HTMLDivElement>;
  containerRef: React.RefObject<HTMLDivElement>;
}

const useCarousel = ({ children, gap }: CarouselProps): CarouselState => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const carouselRef = useRef<HTMLDivElement | null>(null);

  const [carouselWidth, setCarouselWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState(1);

  const childCount = React.Children.count(children);
  const maxPages = Math.ceil((carouselWidth - childCount * gap) / containerWidth);
  const isLeftEnd = page === 1;
  const isRightEnd = page === maxPages;

  useEffect(() => {
    const onResize = throttle(
      () => {
        setOffset(0);
        setPage(1);
        setCarouselWidth(carouselRef.current?.scrollWidth || 0);
        setContainerWidth(containerRef.current?.offsetWidth || 0);
      },
      RESIZE_THROTTLE,
      { leading: false }
    );

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useLayoutEffect(() => {
    setCarouselWidth(carouselRef.current?.scrollWidth || 0);
    setContainerWidth(containerRef.current?.offsetWidth || 0);
  }, [carouselRef.current?.scrollWidth, containerRef.current?.offsetWidth, children]);

  const setPagedOffset = (nextPage: number) => {
    if (nextPage > maxPages) {
      return;
    }

    const prev = nextPage - 1;

    let nextOffset = prev * containerWidth + prev * gap;

    if (nextPage <= 1) {
      nextOffset = 0;
    } else if (nextPage === maxPages) {
      nextOffset = carouselWidth - containerWidth;
    }

    setPage(nextPage);
    setOffset(nextOffset);
  };

  const scrollLeft = (): void => setPagedOffset(page - 1);
  const scrollRight = (): void => {
    setPagedOffset(page + 1);
  };

  return {
    scrollLeft,
    scrollRight,
    setPagedOffset,
    carouselRef,
    containerRef,
    isLeftEnd,
    isRightEnd,
    offset,
    page
  };
};

export default useCarousel;
